import { useState, useEffect } from 'react';
import { Clock, CheckCircle2, XCircle, Loader2, Hourglass } from 'lucide-react';
import client from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import ApprovalChainTimeline from '../../vacation/components/ApprovalChainTimeline';
import {
  NAVY, AMBER, BORDER_COLOR,
  bubbleStyle, navyBtn, fieldStyle,
} from '../../styles/tokens';

const TYPE_LABELS = { vacation: 'Vacaciones', sick: 'Enfermedad', personal: 'Personal' };

const LEVEL_LABELS = {
  1: 'L1 — Supervisor',
  2: 'L2 — Gerente',
  3: 'L3 — Administración',
  4: 'L4 — Recursos Humanos',
  5: 'L5 — Directora',
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-PR', { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function AdminPtoQueue() {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  useEffect(() => { fetchPending(); }, []);

  async function fetchPending() {
    try {
      const res = await client.get('/api/vacation/my-approvals', { params: { view: 'active' } });
      setPending(res.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }

  async function openDetail(requestId) {
    setSelectedId(requestId);
    try {
      const res = await client.get(`/api/vacation/requests/${requestId}`);
      setDetail(res.data);
    } catch {
      setDetail(null);
    }
  }

  async function handleAction(action) {
    if (!selectedId) return;
    setActing(true);
    try {
      await client.post(`/api/vacation/requests/${selectedId}/${action}`, { comments: comments || null });
      setSelectedId(null);
      setDetail(null);
      setComments('');
      await fetchPending();
    } catch (err) {
      alert(err.response?.data?.error || `Error al ${action === 'approve' ? 'aprobar' : 'denegar'}`);
    } finally {
      setActing(false);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
        <Loader2 size={20} className="spin" /> Cargando solicitudes PTO…
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: '700', color: NAVY, margin: 0 }}>
          Aprobaciones PTO
        </h2>
        {pending.filter(r => r.status === 'pending').length > 0 && (
          <span style={{
            background: '#eab308', color: '#fff', borderRadius: 10,
            padding: '2px 10px', fontSize: 12, fontWeight: 700,
          }}>
            {pending.filter(r => r.status === 'pending').length}
          </span>
        )}
        {pending.length > 0 && pending.filter(r => r.status === 'pending').length < pending.length && (
          <span style={{
            background: '#e2e8f0', color: '#64748b', borderRadius: 10,
            padding: '2px 10px', fontSize: 12, fontWeight: 600,
          }}>
            {pending.length} activas
          </span>
        )}
      </div>

      {pending.length === 0 && !selectedId ? (
        <div style={{ ...bubbleStyle, padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
          No hay solicitudes PTO activas.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: detail ? '1fr 1.2fr' : '1fr', gap: 16 }}>
          {/* List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pending.map(r => {
              const isPending = r.status === 'pending';
              const isApproved = r.status === 'approved';
              const borderColor = isPending ? '#eab308' : isApproved ? '#16a34a' : '#94a3b8';
              const isSelected = r.request_id === selectedId;
              return (
                <div
                  key={`${r.request_id}-${r.level}`}
                  onClick={() => openDetail(r.request_id)}
                  style={{
                    ...bubbleStyle, padding: '12px 16px', cursor: 'pointer',
                    borderLeft: `3px solid ${isSelected ? AMBER : borderColor}`,
                    background: isSelected ? '#fffbeb' : '#fff',
                    opacity: isPending ? 1 : 0.85,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    {isPending ? <Clock size={14} color="#eab308" /> :
                     isApproved ? <CheckCircle2 size={14} color="#16a34a" /> :
                     <Hourglass size={14} color="#94a3b8" />}
                    <span style={{ fontWeight: 600, color: NAVY, fontSize: 13 }}>{r.employee_name}</span>
                    <span style={{ fontSize: 11, color: '#64748b' }}>· {TYPE_LABELS[r.type] || r.type}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>
                    {formatDate(r.start_date)} — {formatDate(r.end_date)} · {parseFloat(r.days_requested)} día{parseFloat(r.days_requested) !== 1 ? 's' : ''}
                    {isApproved && (
                      <span style={{ marginLeft: 6, color: '#16a34a', fontWeight: 600 }}>
                        · Aprobado por usted · Nivel {r.current_level}
                      </span>
                    )}
                    {r.status === 'waiting' && (
                      <span style={{ marginLeft: 6, color: '#64748b', fontWeight: 600 }}>
                        · En espera · Nivel {r.current_level}
                      </span>
                    )}
                  </div>
                  <div style={{
                    display: 'inline-block', marginTop: 6,
                    padding: '2px 8px', borderRadius: 10,
                    background: isPending ? '#ede9fe' : isApproved ? '#dcfce7' : '#f1f5f9',
                    color: isPending ? '#6d28d9' : isApproved ? '#16a34a' : '#64748b',
                    fontSize: 10, fontWeight: 600,
                  }}>
                    {isPending ? (LEVEL_LABELS[r.level] || `Nivel ${r.level}`) :
                     `Nivel actual: ${r.current_level}`}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Detail panel */}
          {detail && (
            <div style={{ ...bubbleStyle, padding: '20px 24px' }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: NAVY, marginBottom: 12 }}>
                {detail.user_name} — {TYPE_LABELS[detail.type] || detail.type}
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13, marginBottom: 16 }}>
                <div>
                  <span style={{ color: '#94a3b8', fontSize: 11 }}>Fechas</span>
                  <div style={{ color: NAVY }}>{formatDate(detail.start_date)} — {formatDate(detail.end_date)}</div>
                </div>
                <div>
                  <span style={{ color: '#94a3b8', fontSize: 11 }}>Días</span>
                  <div style={{ color: NAVY, fontWeight: 600 }}>{detail.days_requested}</div>
                </div>
              </div>

              {detail.reason && (
                <div style={{ marginBottom: 16, fontSize: 13 }}>
                  <span style={{ color: '#94a3b8', fontSize: 11 }}>Motivo</span>
                  <div style={{ color: NAVY }}>{detail.reason}</div>
                </div>
              )}

              {/* Chain */}
              {detail.approval_chain?.length > 0 && (
                <div style={{ marginBottom: 16, padding: '12px 0', borderTop: `1px solid ${BORDER_COLOR}` }}>
                  <ApprovalChainTimeline chain={detail.approval_chain} />
                </div>
              )}

              {/* Actions */}
              {(() => {
                const chain = detail.approval_chain || [];
                const pendingRecord = chain.find(r => r.approver_id === user?.id && r.status === 'pending');
                if (!pendingRecord || detail.status !== 'pending') return null;
                return (
                  <div style={{ borderTop: `1px solid ${BORDER_COLOR}`, paddingTop: 16 }}>
                    <textarea
                      value={comments}
                      onChange={e => setComments(e.target.value)}
                      rows={2}
                      style={{ ...fieldStyle, resize: 'vertical', marginBottom: 10 }}
                      placeholder="Comentarios (opcional)"
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => handleAction('approve')} disabled={acting} style={{
                        ...navyBtn, flex: 1, background: '#16a34a', fontSize: 13,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      }}>
                        <CheckCircle2 size={14} /> Aprobar
                      </button>
                      <button onClick={() => handleAction('deny')} disabled={acting} style={{
                        ...navyBtn, flex: 1, background: '#dc2626', fontSize: 13,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      }}>
                        <XCircle size={14} /> Denegar
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
